import React from 'react'
import { useT } from '../../lib/i18n'
import { useKanban } from '../../lib/api'

const SEV_COLOR: Record<string, string> = {
  critical: 'var(--health-crit)',
  high:     'var(--health-warn)',
  medium:   '#60a5fa',
  low:      'var(--text-muted)',
}

interface CardItem { projectId: string; title: string; state: string; at: string }

function KanbanCard({ item, column }: { item: CardItem; column: string }) {
  const t = useT()
  const isCrit = item.state === 'error'
  return (
    <div style={{
      padding: '10px 12px',
      background: 'var(--surface)',
      border: `1px solid ${isCrit ? 'var(--health-crit)' : 'var(--border)'}`,
      borderLeft: `3px solid ${isCrit ? 'var(--health-crit)' : 'var(--accent)'}`,
      borderRadius: 'var(--radius)',
      fontSize: '12px',
      marginBottom: '6px',
    }}>
      <div style={{ fontWeight: 500, color: 'var(--text)', marginBottom: '4px', lineHeight: 1.4 }}>
        {item.title}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span style={{ fontSize: '10px', color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>
          {item.projectId}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: '10px', color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
          {item.at.slice(11, 16)}
        </span>
      </div>
    </div>
  )
}

function Column({ title, color, items, empty }: {
  title: string; color: string; items: CardItem[]; empty: string
}) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        padding: '8px 12px',
        background: 'var(--surface)',
        borderBottom: '2px solid ' + color,
        borderRadius: `${var_radius} ${var_radius} 0 0`,
        marginBottom: '8px',
      }}>
        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: color, flexShrink: 0, display: 'inline-block' }} />
        <strong style={{ fontSize: '12px', fontWeight: 600 }}>{title}</strong>
        <span style={{ marginLeft: 'auto', fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
          {items.length}
        </span>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '0 4px' }}>
        {items.length === 0 ? (
          <div style={{ padding: '16px', textAlign: 'center', fontSize: '12px', color: 'var(--text-dim)' }}>
            {empty}
          </div>
        ) : (
          items.map((item, i) => <KanbanCard key={`${item.projectId}-${i}`} item={item} column={title} />)
        )}
      </div>
    </div>
  )
}

const var_radius = 'var(--radius)'

export default function Kanban() {
  const t = useT()
  const { data, loading } = useKanban()

  if (loading && !data) return (
    <div style={{ padding: '24px', color: 'var(--text-muted)' }}>{t('loading')}</div>
  )

  return (
    <div style={{ display: 'flex', gap: '12px', height: '100%', overflow: 'hidden' }}>
      <Column
        title={t('todo_inprogress')}
        color="var(--accent)"
        items={data?.inProgress ?? []}
        empty="Aucune tâche en cours"
      />
      <Column
        title={t('todo_done')}
        color="var(--health-good)"
        items={data?.done ?? []}
        empty="Aucune tâche terminée"
      />
      <Column
        title={t('todo_errors')}
        color="var(--health-crit)"
        items={data?.errors ?? []}
        empty="Aucune erreur"
      />
    </div>
  )
}
